import { NyuSeriesProperties } from '../../../../../shared/types'

const LABEL_FONT_SIZE = 11

/**
 * Generate letter identifier (A, B, C, etc.) for a dataKey based on actually used series
 */
export function getLetterIdentifier(
    dataKey: string,
    seriesProperties: NyuSeriesProperties,
    usedDataKeys?: string[]
): string {
    // Remove forecast suffix for mapping
    const baseDataKey = dataKey.replace('_forecast', '')

    if (usedDataKeys) {
        // Use the provided list of actually used dataKeys for consistent ordering
        const baseUsedKeys = usedDataKeys.map((key) =>
            key.replace('_forecast', '')
        )
        const uniqueBaseKeys = [...new Set(baseUsedKeys)] // Remove duplicates
        const activeIndex = uniqueBaseKeys.findIndex(
            (key) => key === baseDataKey
        )
        return activeIndex >= 0
            ? String.fromCharCode(65 + activeIndex)
            : baseDataKey
    }

    // Fallback: Find only the series that are actually being used (have data)
    const activeSeries = seriesProperties.filter((s) => s.key)
    const activeIndex = activeSeries.findIndex((s) => s.key === baseDataKey)

    return activeIndex >= 0
        ? String.fromCharCode(65 + activeIndex)
        : baseDataKey
}

type BarLabelProps = {
    x?: number
    y?: number
    width?: number
    height?: number
    dataKey?: string
    value?: number | string
    seriesProperties?: NyuSeriesProperties
    usedDataKeys?: string[]
    highContrastMode?: boolean
}

export function BarLabel({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    dataKey = '',
    value,
    seriesProperties = [],
    usedDataKeys,
    highContrastMode = false,
}: BarLabelProps) {
    const numericValue =
        typeof value === 'string' ? parseFloat(value) : Number(value)

    // Early returns for invalid values
    if (!value || isNaN(numericValue) || numericValue === 0) return null
    if (!highContrastMode) return null

    // Generate letter identifier for the data key
    const letterIdentifier = getLetterIdentifier(
        dataKey,
        seriesProperties,
        usedDataKeys
    )

    return (
        <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#FFFFFF"
            fontSize={LABEL_FONT_SIZE}
            fontWeight="600"
            style={{
                pointerEvents: 'none',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            }}
        >
            {letterIdentifier}
        </text>
    )
}
